import { Flex, Grid, useWindowSize } from '@solinkcorp/solink-components';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { Detail } from '../../Detail';
import { Map } from '../../Map';
import { type Device } from '../../types/device.types';
import { type Event, type VideoLinkItem } from '../../types/event.types';
import {
  formatVideoExpirationDate,
  getEventExpirationDate,
  getLocationName,
} from '../../utils/helperFunctions';
import { AlarmLog } from '../AlarmLog';
import './AlarmDetails.sass';

interface AlarmDetailsProps {
  alarmEvent: Event | null;
  dataReady: boolean;
  device: Device | null;
  error?: string;
  note: string;
  videoExpirationDate: string;
  tokenExpired: boolean;
  handleVideoLinkItemClick: (id: string, timestamp: number, videoUrl: string) => void;
  videoLinkItems: VideoLinkItem[];
}

// Component
const AlarmDetails: React.FC<AlarmDetailsProps> = ({
  alarmEvent,
  device,
  videoExpirationDate,
  tokenExpired,
  note,
  dataReady,
  error,
  handleVideoLinkItemClick,
  videoLinkItems,
}) => {
  const { width } = useWindowSize();
  const locale = useLocale();
  const siteName = getLocationName(alarmEvent?.subtitle ?? '');

  if (!dataReady && !error) {
    return <div className="alarm-details loading"></div>;
  }

  if (!device || !alarmEvent) {
    return <div data-testid="empty-div" />;
  }

  const { timezoneId } = device;
  let abbreviate = false;
  if (width && width < 480) {
    abbreviate = true;
  }

  return (
    <div className="alarm-details" data-testid="alarm-details">
      <Flex justifyContent="center" className="details-wrapper">
        <Grid
          className="alarm-details-grid"
          gridTemplateRows="none"
          gridTemplateColumns="1fr 1fr"
          alignItems="start"
          justifyItems="stretch"
        >
          <Detail type="location" value={siteName} label={locale.spa.location} />
          <Map location={device} />
          {note && <Detail type="note" value={note} label={locale.spa.alarmInstructions} />}
          <Detail
            type="video playback"
            value={formatVideoExpirationDate({
              videoExpirationDate: parseInt(videoExpirationDate),
              timezoneId,
              abbreviate,
            })}
            label={tokenExpired ? locale.spa.videoPlaybackExpired : locale.spa.videoPlaybackExpires}
            location={device}
          />
          <Detail
            type="page expiry"
            value={getEventExpirationDate({
              eventStartTime: alarmEvent.startTime,
              timezoneId,
              abbreviate,
            })}
            label={locale.spa.pageExpires}
            location={device}
          />
          <AlarmLog
            alarmEvent={alarmEvent}
            location={device}
            handleVideoLinkItemClick={handleVideoLinkItemClick}
            timezoneId={timezoneId}
            videoLinkItems={videoLinkItems}
            tokenExpired={tokenExpired}
          />
        </Grid>
      </Flex>
    </div>
  );
};

export default AlarmDetails;
